import { AuthenticationResult, InteractionRequiredAuthError } from "@azure/msal-browser";
import { TranslateService } from "@ngx-translate/core";
import { TokenCredential, TokenCredentialOptions } from "./token.credential";
import { CordovaPlugin } from "../cordova";
import { AlertService } from "../alert/alert.service";
import { AsyncGroup } from "@omni/utility/async-group";
import { TimeoutError, withTimeout } from "@omni/utility/util";

var GENERIC_ERR_MESSAGE = 'Error occurred while executing native method.';
var GENERIC_ERR_CODE = 'NATIVE_METHOD_GENERAL_FAILURE';

// OMNI-60515: silent token renewal must never block forever. On the Electron/desktop
// build the ADAL bridge can fail to invoke its callback after a long OS sleep, leaving
// `acquireTokenSilent` pending indefinitely — which deadlocks the token interceptor and,
// through it, the entire resume Auto Sync. A silent renewal is a cache lookup plus a single
// refresh-token network call, so this ceiling is far above any healthy run on any platform;
// when it trips we reject and let the caller recover (the sync fails and retries next resume)
// instead of hanging until the user force-restarts the app.
const SILENT_TOKEN_TIMEOUT_MS = 60000;

export class CordovaCredential implements TokenCredential {
  private _plugin = new CordovaPlugin('ADALProxy');
  private readonly _ready: Promise<any>;
  readonly ready = () => this._ready;
  #group = new AsyncGroup();
  constructor(
    private options: TokenCredentialOptions,
    private translate: TranslateService,
    private alertService: AlertService,
  ) {
    this._ready = this.init({
      clientId: options.clientId,
      authority: options.authority,
      redirectUri: 'omnipresence://com.omnipresence.io/login'
    })
  }


  async init({ clientId, authority, redirectUri }) {
    await this._plugin.exec('create', {
      auth: {
        clientId: clientId,
        authority,
        redirectUri: redirectUri,
      }
    });
  }


  async getToken(scopes: string | string[]) {
    await this.ready();
    if (!Array.isArray(scopes)) {
      scopes = [scopes];
    }
    const accessTokenRequest = {
      clientId: this.options.clientId,
      authority: this.options.authority,
      scopes,
      loginHint: this.options.username,
    };


    let interactionRequired = false;
    let authResult: AuthenticationResult;
    // OMNI-60515 QA diagnostics: filter the console by "[OMNI-60515]" while reproducing.
    // A "TIMED OUT" line means the silent-renewal hang was caught and the sync was allowed
    // to recover; if the sync still stalls WITHOUT this line, the deadlock is elsewhere.
    const silentStartedAt = Date.now();
    try {
      console.log(`[OMNI-60515] acquireTokenSilent: requesting (scopes=${JSON.stringify(scopes)}, timeoutMs=${SILENT_TOKEN_TIMEOUT_MS})`);
      authResult = await withTimeout(
        this._plugin.exec('acquireTokenSilent', accessTokenRequest),
        SILENT_TOKEN_TIMEOUT_MS,
        'acquireTokenSilent',
      );
      console.log(`[OMNI-60515] acquireTokenSilent: resolved in ${Date.now() - silentStartedAt}ms (hasResult=${!!authResult})`);
      if (!authResult) {
        interactionRequired = true;
        return;
      }
    }
    catch (error) {
      if (error instanceof TimeoutError) {
        // The native bridge never called back — the exact OMNI-60515 hang. We deliberately
        // reject (rather than fall through to the interactive popup, which shares the same
        // possibly-dead bridge) so the stuck Auto Sync settles and retries next resume.
        console.error(`[OMNI-60515] acquireTokenSilent: TIMED OUT after ${Date.now() - silentStartedAt}ms — native ADAL bridge did not call back (likely Electron resume after long sleep). Failing token renewal so the stuck Auto Sync can recover.`);
        throw error;
      }
      if (error?.errorCode === 'CrossPlatformLockError') {
        // OMNI-60515: the ADALProxy already tried to clear the orphaned token-cache lockfile
        // and retry; reaching here means the lock is held by a live process. Reject cleanly —
        // and tell the user — so the resume sync stops instead of retrying the same doomed
        // renewal indefinitely. AlertService dismisses any prior alert, so concurrent renewals
        // can only ever surface one dialog.
        console.error(`[OMNI-60515] acquireTokenSilent: CrossPlatformLockError after ${Date.now() - silentStartedAt}ms — token cache lock could not be recovered.`, error);
        this.alertService.alert(
          this.translate.instant('WARNING'),
          this.translate.instant('SESSION_LOCKED_RESTART_MESSAGE'),
          ['OK'],
        );
        throw error;
      }
      const interactionErrorCodes = [
        'no_current_account',
        'no_account_error',
        "invalid_grant",
        "interaction_required",
        "no_tokens_found",
        "no_account_found",
      ]
      if (interactionErrorCodes.includes(error.errorCode)) {
        console.warn(`[OMNI-60515] acquireTokenSilent: interaction required (errorCode=${error?.errorCode}) after ${Date.now() - silentStartedAt}ms — falling back to interactive`);
        interactionRequired = true;
      } else {
        console.error(`[OMNI-60515] acquireTokenSilent: failed after ${Date.now() - silentStartedAt}ms (errorCode=${error?.errorCode})`, error);
        throw error;
      }
    } finally {
      if (interactionRequired) {
        console.warn('[OMNI-60515] acquireTokenSilent: entering interactive fallback (silent retry + acquireTokenPopup)');
        authResult = await this.#group.with(async () => {
          return (await this._plugin.exec('acquireTokenSilent', accessTokenRequest).catch(() => null))
            ?? (await this._plugin.exec('acquireTokenPopup', accessTokenRequest));
        });

        if (authResult.account?.username?.toLowerCase() !== this.options.username?.toLowerCase()) {
          throw new Error("LOGIN_WE_SIGN_YOU_IN");
        }
      }
      if (authResult) {
        return {
          token: authResult.accessToken,
          expiresOnTimestamp: authResult.expiresOn as unknown as number
        };
      }
    }
  }

  async logout() {}
}

export class IntuneManager {
  private static _plugin = new CordovaPlugin('ADALProxy');
  static initIntune(clientId: string) {
    return this._plugin.exec('initIntune', { clientId });
  }

  static registerAccount(clientId: string, username: string) {
    return this._plugin.exec('registerAccount', { clientId, username });
  }

  static showDiagnostics() {
    return this._plugin.exec('showIntuneDiagnostics', {});
  }
}
